import { makeStyles } from '@material-ui/styles';
import React from 'react';

import { useStyles as usePreVisitDisplayStyles } from 'src/events/styles/preVisitDisplay.styles';
import type { Patient } from 'src/shared/stores/resource';

const INTAKE_VISIT_NOTE_TYPES = new Set([
  'care_advocate_intake',
  'care_advocate_drop_in_intake',
  'prescriber_drop_in_consolidated_intake',
  'prescriber_drop_in_intake',
  'prescriber_initial',
]);
const HAS_PRIOR_TREATMENT_EXPERIENCE = 'has prior treatment experience';
const NO_PRIOR_TREATMENT_EXPERIENCE = 'no prior treatment experience';

export const PrimaryConcern: React.FC<{
  patient?: Patient;
  subType: string | null;
}> = ({ patient, subType }) => {
  const preVisitDisplayStyles = usePreVisitDisplayStyles();
  const styles = useStyles();
  const primaryConcernLines = getPrimaryConcernLines(patient);

  if (!shouldRenderPrimaryConcern({ patient, subType, primaryConcernLines })) {
    return null;
  }

  return (
    <div className={preVisitDisplayStyles.sectionContainer} data-testid="primary-concern">
      <div className={preVisitDisplayStyles.sectionLabel}>Primary concern</div>
      <div className={styles.listContainer}>
        <ul>
          {primaryConcernLines.map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

function getPrimaryConcernLines(patient?: Patient): string[] {
  if (!patient) {
    return [];
  }

  const concerns = patient.primaryConcernCheck ?? [];
  const showsOpioidConcern = concerns.some(primaryConcern => /opioid/i.test(primaryConcern));
  const showsAlcoholConcern = concerns.some(primaryConcern => /alcohol/i.test(primaryConcern));

  const lines: string[] = [];

  if (
    showsOpioidConcern &&
    patient.opioidTreatmentExperience != null &&
    patient.bupeFamiliarity != null
  ) {
    const opioidDetails: string[] = [];

    if (patient.opioidTreatmentExperience) {
      opioidDetails.push(HAS_PRIOR_TREATMENT_EXPERIENCE);
    } else {
      opioidDetails.push(NO_PRIOR_TREATMENT_EXPERIENCE);
    }

    const bupeFamiliarityText = formatBupeFamiliarity(patient.bupeFamiliarity);
    if (bupeFamiliarityText) {
      opioidDetails.push(bupeFamiliarityText);
    }

    lines.push(formatPrimaryConcernLine('Opioid use', opioidDetails));
  }

  if (showsAlcoholConcern && patient.alcoholTreatmentExperience != null) {
    const alcoholDetails: string[] = [];

    if (patient.alcoholTreatmentExperience) {
      alcoholDetails.push(HAS_PRIOR_TREATMENT_EXPERIENCE);
    } else {
      alcoholDetails.push(NO_PRIOR_TREATMENT_EXPERIENCE);
    }

    lines.push(formatPrimaryConcernLine('Alcohol use', alcoholDetails));
  }

  return lines;
}

function shouldRenderPrimaryConcern({
  patient,
  subType,
  primaryConcernLines,
}: {
  patient?: Patient;
  subType: string | null;
  primaryConcernLines: string[];
}): boolean {
  return (
    !!patient && !!subType && INTAKE_VISIT_NOTE_TYPES.has(subType) && primaryConcernLines.length > 0
  );
}

function formatPrimaryConcernLine(label: string, details: string[]): string {
  return details.length > 0 ? `${label}: ${details.join(', ')}` : `${label}:`;
}

function formatBupeFamiliarity(bupeFamiliarity: string | null | undefined): string | null {
  if (!bupeFamiliarity) {
    return null;
  }

  const trimmedValue = bupeFamiliarity.trim();

  if (/taking/i.test(trimmedValue)) {
    return 'currently taking buprenorphine';
  }

  if (/never\b.*\btaken/i.test(trimmedValue)) {
    return 'has never taken buprenorphine';
  }

  if (/have\b.*\btaken/i.test(trimmedValue)) {
    return 'has previously taken buprenorphine';
  }

  return bupeFamiliarity;
}

const useStyles = makeStyles({
  listContainer: {
    '& ul': {
      marginTop: 5,
      marginBottom: 0,
    },
  },
});

export const __test__ = { formatBupeFamiliarity };
